import { Link } from "react-router";
import { type Period, SkyBand, usePeriod } from "./Sky.tsx";
import { Requirements, Steps } from "./Steps.tsx";
import { TryAtzmai } from "./TryAtzmai.tsx";

const showcase = [
  {
    img: "website",
    title: "Ask for a website, and watch it being made",
    body: "Say what you want in your own words. Atzmai writes it and shows it beside the chat, and the page changes as you ask for changes.",
  },
  {
    img: "letter",
    title: "Letters, budgets and documents, ready to use",
    body: "A letter to the landlord, a month's budget, a CV. Each is saved in your Documents and shown right there, to read before you open it anywhere else.",
  },
  {
    img: "tasks",
    title: "It remembers for you",
    body: "Reminders and tasks at the time you choose. Anything that posts or sends a message is shown to you first, and nothing goes without your yes.",
  },
];

const points = [
  ["It works without the internet", "The assistant runs on the computer itself. Your documents stay on your machine."],
  ["Documents are checked first", "Files are checked before any AI reads them, so hidden instructions can't take over."],
  ["Updates can be undone", "Updates install by themselves, with a snapshot first, so a bad one can be rolled back."],
  ["Children have their own space", "Their own accounts, a safe web, and a daily time limit a parent sets."],
];

const SKY_WORDS: Record<Period, string> = { dawn: "a dawn sky", day: "a daytime sky", dusk: "an evening sky", night: "a night sky" };

export function Home() {
  const period = usePeriod();
  return (
    <div className="flex flex-col gap-28">
      <SkyBand period={period} />

      <section className="flex flex-col gap-14 pt-6 md:pt-14">
        <div className="on-sky flex max-w-[44rem] flex-col gap-6">
          <h1 className="text-[clamp(40px,6.4vw,68px)] leading-[1.04] tracking-[-.02em]">Your computer, with a helper built in.</h1>
          <p className="max-w-[36rem] text-[21px] leading-snug text-ink">
            Ask for a letter, a budget or a website, and Atzmai makes it right there on the computer. Free, private, and safe for the whole family.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/download" className="btn btn-primary min-h-12 px-6 text-base">Download Atzmai OS</Link>
            <a href="#how" className="btn min-h-12 border-ink/50 px-6 text-base text-ink">How to install</a>
          </div>
        </div>
        <figure className="m-0 flex flex-col gap-3">
          {/* on a phone the whole screen shrinks until the dock is too small to tap, so phones get the picture */}
          <div className="max-sm:hidden"><TryAtzmai /></div>
          <div className="sm:hidden"><Screen name="home" alt="The home screen of Atzmai OS" /></div>
          <figcaption className="text-[15px] text-ink-3">
            <span className="sm:hidden">The home screen of Atzmai OS. Open this page on a computer to click around it.</span>
            <span className="max-sm:hidden">Have a look around: the dock opens each app, and Back to home brings you here again. The clock is yours, and the sky is {SKY_WORDS[period]}, as it is where you are.</span>
          </figcaption>
        </figure>
      </section>

      <section className="flex flex-col gap-20" aria-label="What you can do">
        {showcase.map((s, i) => (
          <div key={s.img} className="grid items-center gap-8 md:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] md:gap-14">
            <div className={`flex max-w-[30rem] flex-col gap-3 ${i % 2 ? "md:order-2" : ""}`}>
              <h2 className="text-[clamp(26px,3vw,34px)] leading-tight">{s.title}</h2>
              <p className="text-ink-2">{s.body}</p>
            </div>
            <Screen name={s.img} alt={s.title} />
          </div>
        ))}
      </section>

      <section className="grid gap-10 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] md:gap-14">
        <h2 className="text-[clamp(26px,3vw,34px)] leading-tight">Safe by design, for everyone at home</h2>
        <dl className="m-0 grid gap-x-12 gap-y-8 sm:grid-cols-2">
          {points.map(([title, body]) => (
            <div key={title} className="flex flex-col gap-1.5">
              <dt className="font-display text-[19px] font-medium text-ink">{title}</dt>
              <dd className="m-0 text-ink-2">{body}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section id="how" className="flex scroll-mt-8 flex-col gap-10">
        <div className="flex max-w-[36rem] flex-col gap-2">
          <h2 className="text-[clamp(26px,3vw,34px)] leading-tight">How to install</h2>
          <p className="text-ink-2">About half an hour, most of it waiting for the download. You can try Atzmai from the stick without changing anything on the computer.</p>
        </div>
        <Steps />
        <Requirements />
        <div>
          <Link to="/download" className="btn btn-primary min-h-12 px-6 text-base">Download Atzmai OS</Link>
        </div>
      </section>
    </div>
  );
}

/** A real screenshot of Atzmai OS, framed as a screen. */
function Screen({ name, alt, eager = false }: { name: string; alt: string; eager?: boolean }) {
  return (
    <img
      src={`/screens/${name}.webp`}
      srcSet={`/screens/${name}-small.webp 800w, /screens/${name}.webp 1600w`}
      sizes="(min-width: 1100px) 1068px, 100vw"
      width={1600}
      height={900}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      className="h-auto w-full rounded-[14px] shadow-[0_50px_120px_-40px_rgb(7_8_34/.9),0_0_0_1px_rgb(255_255_255/.12)]"
    />
  );
}
